import { Currency, Modal, SubmitButton } from "@xola/ui-kit";
import { FormField, nullableNumber } from "common";
import { FormikValues } from "formik";
import React, { useEffect } from "react";
import { Except } from "type-fest";
import { object } from "yup";
import { useSeller } from "../../hooks/useAccount";
import { numberFormat } from "../../utils/numbers";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { CurrencyInputField } from "../Field/CurrencyInputField";

const getValidationSchema = (minAmount: number, formattedMinAmount: string) => {
    return object({
        amount: nullableNumber().min(minAmount, `must be greater or equal to ${formattedMinAmount}`),
    });
};

interface CashCalculatorModalProps {
    isLoading?: boolean;
    minAmount: number;
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (value: number) => void;
}

export const CashCalculatorModal = ({ isOpen, isLoading, minAmount, onClose, onSubmit }: CashCalculatorModalProps) => (
    <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent isLoading={isLoading} minAmount={minAmount} onClose={onClose} onSubmit={onSubmit} />
    </Modal>
);

const ModalContent = ({
    isLoading = false,
    minAmount,
    onClose,
    onSubmit,
}: Except<CashCalculatorModalProps, "isOpen">) => {
    const seller = useSeller();

    useEffect(() => {
        const amountInput = document.querySelector("#amount-input")!;

        if (amountInput) {
            (amountInput as HTMLInputElement)?.focus();
        }
    }, []);

    const submitButtonContent = (value: number | null) => {
        if (!value) {
            return "Exact Cash Tendered";
        }

        if (value < minAmount) {
            return `Minimum ${minAmount}`;
        }

        return "Done";
    };

    const handleSubmit = (values: FormikValues) => {
        onSubmit(values.amount);
    };

    return (
        <>
            <Modal.Header>Enter Cash Tendered</Modal.Header>

            <XolaFormik
                validateOnChange
                validateOnBlur={false}
                initialValues={{ amount: null }}
                validationSchema={getValidationSchema(minAmount, numberFormat(minAmount, seller.currency))}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, values, submitForm }) => {
                    const isSubmitDisabled = values.amount && values.amount < minAmount;
                    return (
                        <>
                            <Modal.Body>
                                <fieldset disabled={isLoading}>
                                    <FormField name="amount" label="Amount">
                                        <CurrencyInputField
                                            autoFocus
                                            id="amount-input"
                                            onKeyDown={(e) => handleKeyDown(e, submitForm)}
                                        />
                                    </FormField>
                                </fieldset>
                            </Modal.Body>

                            <h2 className="flex justify-between">
                                <span>Change Due</span>

                                <Currency currency={seller.currency}>
                                    {values.amount !== null && values.amount > Math.abs(minAmount)
                                        ? values.amount - Math.abs(minAmount)
                                        : 0}
                                </Currency>
                            </h2>

                            <Modal.Footer>
                                <SecondaryButton disabled={isLoading} onClick={onClose}>
                                    Cancel
                                </SecondaryButton>

                                <SubmitButton isLoading={isLoading} disabled={isSubmitDisabled} onClick={handleSubmit}>
                                    {submitButtonContent(values.amount)}
                                </SubmitButton>
                            </Modal.Footer>
                        </>
                    );
                }}
            </XolaFormik>
        </>
    );
};

const handleKeyDown = (event: React.KeyboardEvent, submitForm: () => void) => {
    if (event.key === "Enter") {
        event.preventDefault();
        submitForm();
    }
};
